<template>
  <div style="padding: 16px; background: #F8F8F9;">
    <div class="cm-panel">
      <div class="cm-panel-head">
        <div class="cm-panel-title">Unassigned Tasks</div>
        <div class="cm-panel-head-actions">
          <span v-if="data && data.total_unassigned > 0" style="font-size: 11.5px; color: #B45309;">
            <strong>{{ data.total_unassigned }}</strong> unassigned<span v-if="data.total_assignable > 0"> · <span style="color: #B9431B; font-weight: 600;">{{ data.total_assignable }} fixable</span></span>
          </span>
          <button class="cm-icon-btn" @click="fetchHealth" :disabled="loading" title="Refresh">
            <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

      <!-- No unassigned -->
      <div v-else-if="data && data.total_unassigned === 0" class="cm-empty">
        <FontAwesomeIcon :icon="['fas', 'check-circle']" class="ico" style="color: #1F9A63;" />
        <div class="msg">All tasks are assigned</div>
      </div>

      <!-- Grouped by program -->
      <div v-else-if="data && programGroups.length">
        <div v-for="(group, gi) in programGroups" :key="group.metric_id" style="border-bottom: 1px solid #F0F0F1;">
          <!-- Program header (collapsible) -->
          <div style="display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; cursor: pointer;"
            @click="toggleGroup(gi)">
            <span style="display: flex; align-items: center; gap: 8px; font-size: 12.5px; font-weight: 600; color: #353537;">
              <FontAwesomeIcon :icon="['fas', openGroups[gi] ? 'chevron-down' : 'chevron-right']" style="font-size: 9px; color: #A5A5A6; width: 10px;" />
              {{ group.metric_name }}
            </span>
            <span class="cm-count-pill warn">{{ group.total_unassigned }}</span>
          </div>

          <!-- Expanded detail -->
          <div v-if="openGroups[gi]" style="padding: 0 16px 12px 36px;">
            <!-- Stages -->
            <div v-for="stage in group.stages" :key="stage.task_type_id"
              style="display: flex; align-items: center; justify-content: space-between; padding: 5px 0; font-size: 11.5px; color: #626264;">
              <span>{{ stage.task_type_name }}</span>
              <span><span style="color: #B45309; font-weight: 600;">{{ stage.unassigned }}</span> <span style="color: #A5A5A6;">/ {{ stage.total }}</span></span>
            </div>

            <!-- Orphan practices callout -->
            <div v-if="group.orphan_practices.length" style="margin-top: 8px; background: #FDF4E7; border: 1px solid #F6E2BE; border-radius: 6px; padding: 8px 10px;">
              <div style="font-size: 11.5px; font-weight: 600; color: #92400E; margin-bottom: 4px;">
                <FontAwesomeIcon :icon="['fas', 'exclamation-circle']" style="margin-right: 6px;" />Practices missing rules ({{ group.orphan_practices.length }})
              </div>
              <div v-for="p in group.orphan_practices" :key="p.practice_name"
                style="display: flex; align-items: center; justify-content: space-between; padding: 3px 0; font-size: 11.5px;">
                <span class="sensitive" style="color: #626264;">{{ p.practice_name }}</span>
                <span style="color: #A5A5A6;">{{ p.patient_count }} patients</span>
              </div>
            </div>

            <!-- No practice callout -->
            <div v-if="group.no_practice_count > 0"
              style="margin-top: 8px; background: #FDF1EF; border: 1px solid #F6D2CC; border-radius: 6px; padding: 8px 10px; font-size: 11.5px; font-weight: 600; color: #B42318;">
              {{ group.no_practice_count }} patients not linked to any practice
            </div>
          </div>
        </div>

        <!-- Footer bar -->
        <div style="display: flex; align-items: center; justify-content: space-between; padding: 11px 16px; background: #F8F8F9;">
          <span style="font-size: 11.5px; color: #8B8B8D;">{{ data.total_assignable }} tasks can be auto-assigned from existing rules.</span>
          <button class="cm-btn cm-btn-primary cm-btn-sm" @click="executeAssign" :disabled="assigning || !data.total_assignable">
            {{ assigning ? 'Assigning…' : `Assign ${data.total_assignable} Tasks` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Result modal -->
    <Teleport to="body">
      <div v-if="assignResult" class="cm-overlay" @click.self="assignResult = null">
        <div class="cm-modal">
          <div class="cm-modal-head">
            <h3>Assignment Result</h3>
            <button class="cm-icon-btn" @click="assignResult = null"><FontAwesomeIcon :icon="['fas', 'xmark']" /></button>
          </div>
          <div class="cm-modal-body">
            <div style="font-size: 26px; font-weight: 700; color: #353537;">{{ assignResult.tasks_moved || 0 }} tasks assigned</div>
          </div>
          <div class="cm-modal-foot">
            <button class="cm-btn cm-btn-primary" @click="assignResult = null">Done</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { listCareManagementPrograms } from '@/services/api/careManagementPrograms';

export default {
  name: 'AssignmentHealth',
  components: { FontAwesomeIcon },
  emits: ['health-loaded', 'assigned'],
  data() {
    return {
      loading: false,
      data: null,
      cmMetricIds: [],
      openGroups: { 0: true },
      assigning: false,
      assignResult: null,
    };
  },
  computed: {
    programGroups() {
      if (!this.data || !this.data.stages) return [];
      const groups = {};
      for (const s of this.data.stages) {
        if (s.unassigned === 0) continue;
        if (!groups[s.metric_id]) {
          groups[s.metric_id] = {
            metric_id: s.metric_id, metric_name: s.metric_name, metric_slug: s.metric_slug,
            total_unassigned: 0, stages: [], orphan_practices: [], no_practice_count: 0,
          };
        }
        const g = groups[s.metric_id];
        g.total_unassigned += s.unassigned;
        g.stages.push(s);
        g.no_practice_count += s.no_practice_count || 0;
        for (const p of (s.orphan_practices || [])) {
          const existing = g.orphan_practices.find(x => x.practice_id === p.practice_id);
          if (existing) existing.patient_count += p.patient_count;
          else g.orphan_practices.push({ ...p });
        }
      }
      for (const g of Object.values(groups)) g.orphan_practices.sort((a, b) => b.patient_count - a.patient_count);
      return Object.values(groups).sort((a, b) => b.total_unassigned - a.total_unassigned);
    },
  },
  methods: {
    async fetchCmMetricIds() {
      // Tenant-scoped: pull this tenant's configured CM programs dynamically.
      // No hardcoded IDs — mirrors TeamAssignments.vue so IHCI (and any tenant)
      // gets its own CM metrics rather than Vytalize's.
      try {
        const metrics = await listCareManagementPrograms();
        this.cmMetricIds = metrics.map(m => m.id);
      } catch (e) {
        console.error('Error fetching care management metrics:', e);
        this.cmMetricIds = [];
      }
    },
    async fetchHealth() {
      this.loading = true;
      this.assignResult = null;
      try {
        if (!this.cmMetricIds.length) await this.fetchCmMetricIds();
        if (!this.cmMetricIds.length) { this.data = null; return; }
        const { data } = await axios.get('/api/team-assignments/health/', {
          params: { metric_ids: this.cmMetricIds },
        });
        this.data = data;
        this.$emit('health-loaded', data);
      } catch (e) { console.error('Assignment health fetch failed:', e); }
      finally { this.loading = false; }
    },
    toggleGroup(gi) {
      this.openGroups = { ...this.openGroups, [gi]: !this.openGroups[gi] };
    },
    async executeAssign() {
      this.assigning = true;
      this.assignResult = null;
      try {
        const { data } = await axios.post('/api/team-assignments/rebalance/', {
          dry_run: false, unassigned_only: true, metric_ids: this.cmMetricIds,
        });
        this.assignResult = { tasks_moved: data.tasks_moved };
        this.fetchHealth();
        this.$emit('assigned');
      } catch (e) {
        this.assignResult = { tasks_moved: 0, error: e.response?.data?.error || 'Assignment failed' };
      } finally { this.assigning = false; }
    },
  },
  mounted() { this.fetchHealth(); },
};
</script>
